/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable @typescript-eslint/no-explicit-any */

import Autoplay from "embla-carousel-autoplay";
import * as React from "react";
import { useEffect, useState } from "react";
import { BsYoutube } from "react-icons/bs";
import ReactPlayer from "react-player";
import { Carousel, CarouselContent, CarouselItem } from "~/components/ui/carousel";
import useIsClient from "~/hook/useIsClient";
import useStopCurrentRunningVideoAudio from "~/hook/useStopCurrentRunningVideoAudio";
import { useVideoManager } from "~/hook/useVideoManager";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import { useAppDispatch } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { FALLBACK_IMAGE_URL, youtubeLinkPrefix } from "~/utils/constant";
import { isIOS } from "~/utils/helper-funcations";
import ClientOnly from "../shared-components/ClentOnly";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import LightGalleryWrapper from "../shared-components/LightGalleryWrapper";

type ImageData = {
  id: number;
  url: {
    small: string;
    medium: string;
    large: string;
    hd: string;
    original: string;
  };
  alt: string;
  blur_hash: string;
  img_height: number;
  video_id: string | null;
};

type CustomCarouselProps = {
  images: ImageData[];
};

const DailyDarshanCarousel: React.FC<CustomCarouselProps> = ({ images }) => {
  const dispatch = useAppDispatch();
  const { removeVideoPlay } = useStopCurrentRunningVideoAudio();
  const plugin = React.useRef(Autoplay({ delay: 2000, stopOnInteraction: true }));
  const uniqId = React.useId();
  const componentId = `video-${uniqId}`;
  const { playVideo, pauseVideo, isVideoPlaying, pauseAllVideos, currentVideo } = useVideoManager(componentId);
  const isClient = useIsClient();

  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [videoPlayersActive, setVideoPlayersActive] = useState<Set<number>>(new Set());
  const [isAutoplayPaused, setIsAutoplayPaused] = useState(false);
  const [pausedVideoIndex, setPausedVideoIndex] = useState<number | null>(null);

  const activateVideoPlayer = (index: number, videoId: string | null) => {
    if (!videoId) return;
    setVideoPlayersActive((prev) => new Set(prev).add(index));
    setIsAutoplayPaused(true);
    if (plugin.current) {
      plugin.current.stop();
    }
  };

  const deactivateVideoPlayer = (index: number) => {
    setVideoPlayersActive((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);

      if (plugin.current) {
        plugin.current.play();
      }

      return newSet;
    });
  };

  const handleDotClick = (index: number) => {
    // Pause any currently playing videos before navigating
    pauseAllVideos();
    setVideoPlayersActive(new Set());

    setCurrentIndex(index);
    carouselApi?.scrollTo(index);
  };

  useEffect(() => {
    if (!carouselApi) return;
    let timeout: NodeJS.Timeout;
    const onSelect = () => {
      setCurrentIndex(carouselApi.selectedScrollSnap());
      // Only restart autoplay if no videos are active
      if (videoPlayersActive.size === 0 && plugin.current && images.length > 1) {
        plugin?.current?.stop();
        timeout = setTimeout(() => {
          plugin?.current?.play();
        }, 1000);
      }
    };

    onSelect();
    carouselApi.on("select", onSelect);

    return () => {
      carouselApi.off("select", onSelect);
      clearTimeout(timeout);
    };
  }, [carouselApi, videoPlayersActive]);

  // Stop autoplay when any video player is active
  useEffect(() => {
    if (videoPlayersActive.size > 0) {
      plugin.current?.stop?.();
    }
  }, [videoPlayersActive]);

  useEffect(() => {
    return () => {
      pauseAllVideos();
    };
  }, []);

  return (
    <ClientOnly>
      <>
        <div className={cn("w-full flex flex-col gap-between-dot-and-content px-3 ")}>
          <Carousel
            plugins={plugin.current ? [plugin.current] : []}
            className={`overflow-hidden w-full h-auto`}
            setApi={setCarouselApi}
            opts={{ loop: true }}
          >
            <CarouselContent>
              {images?.map((data, index) => {
                return (
                  <CarouselItem key={index} className="flex justify-center items-center ">
                    {data?.video_id ? (
                      <div className="aspect-[16/9] w-full relative rounded-xl" key={`${data?.video_id}`}>
                        {!videoPlayersActive.has(index) && !isVideoPlaying(data?.video_id || "", index) ? (
                          // Thumbnail with YouTube icon
                          <div className="w-full h-full relative cursor-pointer group" onClick={() => activateVideoPlayer(index, data?.video_id)}>
                            <img
                              src={getYoutubeVideoThumbnail(data.video_id, "medium")}
                              alt={`${data.alt || "Video thumbnail"}`}
                              className="w-full h-full object-cover select-none rounded-xl"
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = data.url?.large || FALLBACK_IMAGE_URL;
                              }}
                            />
                            <div className="absolute inset-0 bg-black/20 rounded-xl" />
                            <div
                              onClick={() => {
                                playVideo(data.video_id!, index);
                                dispatch(changeIsPlayingState(false));
                                removeVideoPlay();
                              }}
                              className="absolute inset-0 flex items-center justify-center "
                            >
                              <BsYoutube size={50} fill="red" className="text-white" />
                            </div>
                          </div>
                        ) : pausedVideoIndex === index ? (
                          // Thumbnail with YouTube icon (Paused view)
                          <div className="w-full h-full relative cursor-pointer group" onClick={() => activateVideoPlayer(index, data?.video_id)}>
                            <img
                              src={getYoutubeVideoThumbnail(data.video_id, "medium")}
                              alt={`${data.alt || "Video thumbnail"}`}
                              className="w-full h-full object-cover select-none rounded-xl"
                              onError={({ currentTarget }) => {
                                currentTarget.onerror = null;
                                currentTarget.src = data.url?.large || FALLBACK_IMAGE_URL;
                              }}
                            />
                            <div className="absolute inset-0 bg-black/20 rounded-xl" />
                            <div
                              onClick={() => {
                                setPausedVideoIndex(null);
                                playVideo(data.video_id!, index);
                                dispatch(changeIsPlayingState(false));
                                removeVideoPlay();
                              }}
                              className="absolute inset-0 flex items-center justify-center"
                            >
                              <BsYoutube size={50} fill="red" className="text-white" />
                            </div>
                          </div>
                        ) : (
                          // Actual ReactPlayer
                          <ReactPlayer
                            url={`${youtubeLinkPrefix}${data.video_id}`}
                            width="100%"
                            height="100%"
                            className="rounded-xl overflow-hidden"
                            controls
                            muted={isIOS() ? true : false}
                            playing={isVideoPlaying(data?.video_id || "", index)}
                            onReady={() => {
                              playVideo(data.video_id!, index);
                              dispatch(changeIsPlayingState(false));
                              removeVideoPlay();
                            }}
                            onPause={() => {
                              if (data.video_id && isVideoPlaying(data.video_id, index)) {
                                pauseVideo(data.video_id, index);
                              }
                              setPausedVideoIndex(index);
                            }}
                            onPlay={() => {
                              setPausedVideoIndex(null);
                            }}
                            onEnded={() => {
                              if (data.video_id) {
                                pauseVideo(data.video_id, index);
                                deactivateVideoPlayer(index);
                              }
                              setPausedVideoIndex(null);
                            }}
                          />
                        )}
                      </div>
                    ) : (
                      <div
                        className="flex justify-center items-center mx-auto cursor-pointer rounded-xl"
                        onClick={() => {
                          setLightboxIndex(index);
                          plugin.current?.stop?.();
                        }}
                      >
                        <ImageWithSkeleton
                          url={{
                            small: data.url.medium,
                            medium: data.url.medium,
                            large: data.url.large,
                            hd: data.url.hd,
                            original: data.url.original,
                          }}
                          alt={`${data.alt || "Daily Darshan Image"}`}
                          blur_hash={data.blur_hash}
                          className="object-scale-down max-h-[450px] h-auto w-auto max-w-full mx-auto bg-white rounded-xl "
                          height={400}
                          roundedClass="rounded-xl"
                          isPreloaded={!isClient}
                        />
                      </div>
                    )}
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>

          <div className=" mx-auto w-full flex justify-center gap-x-1 ">
            {images?.map((_, index) => (
              <div key={index} className="flex  justify-center items-center cursor-pointer" onClick={() => handleDotClick(index)}>
                <button
                  key={index}
                  className={cn(
                    "w-3 h-1 rounded-full transition-all duration-300 ease-in-out",
                    currentIndex === index ? "bg-textHeadingColor w-8 shadow-lg" : "bg-lightbackground hover:scale-110",
                  )}
                  aria-label={`Go to heritage ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
        <LightGalleryWrapper
          images={images?.map((img) => ({
            id: img.id,
            url: img.url
              ? {
                  small: img.url.small,
                  medium: img.url.medium,
                  large: img.url.large,
                  hd: img.url.hd,
                  original: img.url.original,
                }
              : {
                  small: FALLBACK_IMAGE_URL,
                  medium: FALLBACK_IMAGE_URL,
                  large: FALLBACK_IMAGE_URL,
                  hd: FALLBACK_IMAGE_URL,
                  original: FALLBACK_IMAGE_URL,
                },
            alt: img.alt || "Daily Darshan Image",
            photoName: img.alt || "Daily Darshan Image",
          }))}
          indexToOpen={lightboxIndex}
          setLightBoxIndex={() => {
            setLightboxIndex(null);
            plugin.current?.play?.();
          }}
        />
      </>
    </ClientOnly>
  );
};

export default DailyDarshanCarousel;
